/**
 * Mandatory MFA Setup Page
 * Users must complete MFA enrollment before accessing the dashboard
 * This page cannot be skipped
 */

import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/lib/auth-context';
import { MFAEnrollment } from '@/components/auth/MFAEnrollment';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2 } from 'lucide-react';
import { SEO } from '@/components/SEO';

export default function SetupMFAPage() {
  const [, setLocation] = useLocation();
  const { isAuthenticated, isLoading, checkMFAStatus, signOut } = useAuth();

  // Check if user already has MFA enabled
  useEffect(() => {
    const checkExistingMFA = async () => {
      if (isAuthenticated && !isLoading) {
        const mfaStatus = await checkMFAStatus();
        if (mfaStatus.enabled) {
          // Already has MFA, redirect to dashboard
          setLocation('/dashboard');
        }
      }
    };
    checkExistingMFA();
  }, [isAuthenticated, isLoading, checkMFAStatus, setLocation]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setLocation('/login');
    }
  }, [isLoading, isAuthenticated, setLocation]);

  const handleMFASuccess = () => {
    // MFA setup complete, redirect to dashboard
    const returnTo = sessionStorage.getItem('returnTo') || '/dashboard';
    sessionStorage.removeItem('returnTo');
    setLocation(returnTo);
  };

  // No cancel option - MFA is mandatory
  // But allow signing out if user wants to use a different account
  const handleSignOut = async () => {
    await signOut();
    setLocation('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <SEO
        title="Set Up Two-Factor Authentication - Prozpector"
        description="Secure your Prozpector account with two-factor authentication."
        url="https://prozpector.com/setup-mfa"
      />
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/logo-square.svg"
              alt="Prozpector"
              className="w-16 h-16 rounded-xl shadow-lg"
            />
          </div>
          <img
            src="/logo-wordmark.svg"
            alt="Prozpector"
            className="h-10 mx-auto mb-4"
          />
        </div>

        {/* Security Notice */}
        <Alert className="mb-6 border-primary-200 bg-primary-50">
          <Shield className="h-4 w-4 text-primary-600" />
          <AlertDescription className="text-primary-800">
            <strong>Security Required:</strong> Two-factor authentication is mandatory to protect your account and data.
          </AlertDescription>
        </Alert>

        {/* MFA Enrollment Component - No cancel option */}
        <MFAEnrollment onSuccess={handleMFASuccess} />

        {/* Sign out option */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Wrong account?{' '}
            <button
              onClick={handleSignOut}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Sign out
            </button>
          </p>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Your security is our priority</p>
        </div>
      </div>
    </div>
  );
}
